import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import moment from "moment";
import { FaCalendarAlt, FaClipboardList, FaEye } from "react-icons/fa";
import styles from "../../../schoolCockpit/master/academicConfiguration/AcademicCreate.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import { CustomButton } from "../../../../components";
import ScheduleTable from "../../../../components/Scheduletable/Scheduletable";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";

/** Safely format a date to YYYY-MM-DD. */
const formatDate = (value) => {
  if (!value) return "";
  if (typeof value === "object" && typeof value.format === "function") {
    if (typeof value.isValid === "function" && !value.isValid()) return "";
    return value.format("YYYY-MM-DD");
  }
  return moment(value).format("YYYY-MM-DD");
};

/** Normalize class/subject strings for safe comparison. */
const normalize = (val) =>
  String(val || "")
    .trim()
    .toUpperCase();

/** Safely lowercase anything (numbers, nulls, etc.) */
const normalizeStatus = (val) => {
  if (val == null) return "";
  return String(val).trim().toLowerCase();
};

/**
 * Map raw status code from the timetable API into a UI status key
 * that ScheduleTable uses for cell coloring.
 *
 *   null | "" | 0 | 6  -> ""             (white / no special color)
 *   1                  -> "submitted"
 *   2                  -> "approved"
 *   3                  -> "rejected"
 *   4                  -> "resubmitted"
 *   5                  -> "completed"
 *
 * Strings already in key form (e.g. "submitted") are passed through
 * lower-cased so the function is idempotent.
 */
const STATUS_CODE_MAP = {
  1: "submitted",
  2: "approved",
  3: "rejected",
  4: "resubmitted",
  5: "completed",
};

const resolveStatusKey = (rawStatus) => {
  if (rawStatus == null || rawStatus === "") return "";

  // Numeric / numeric-string codes
  const asNum = Number(rawStatus);
  if (!Number.isNaN(asNum)) {
    if (asNum === 0 || asNum === 6) return "";
    if (STATUS_CODE_MAP[asNum]) return STATUS_CODE_MAP[asNum];
    return "";
  }

  // Already a string key
  return normalizeStatus(rawStatus);
};

/**
 * Get a stable unique identifier from a lesson-plan row returned by
 * /book/lessonPlanDraftList.
 */
const getPlanId = (plan) => {
  if (!plan) return null;
  const candidates = [
    plan.id,
    plan.lesson_plan_id,
    plan.lessonPlanId,
    plan.def_id,
    plan.defId,
    plan.pk,
    plan._id,
    plan.uuid,
  ];
  for (const c of candidates) {
    if (c !== undefined && c !== null && c !== "") return c;
  }
  return null;
};

/** Transform timetable API response → ScheduleTable schedule rows. */
const transformTimetableResponse = (timetableData) => {
  if (!timetableData || !Array.isArray(timetableData.timetable)) return [];
  return timetableData.timetable.map((dayRow) => ({
    day: dayRow.day_name,
    date: moment(dayRow.date).format("MMM - D"),
    rawDate: dayRow.date,
    periods: (dayRow.periods || []).map((p) => {
      const hasClass = p.class_name && p.subject_name;
      const statusKey = resolveStatusKey(p.status);
      return {
        periodId: String(p.period),
        subject: hasClass ? p.subject_name : null,
        section: hasClass
          ? `${p.class_name}${p.section_id ? ` - ${p.section_id}` : ""}`
          : null,
        status: statusKey,
        rawStatus: p.status,
        hm_id: p.hm_id ?? null,
        class_id: p.class_id,
        class_name: p.class_name,
        section_id: p.section_id,
        subject_name: p.subject_name,
        course_id: p.course_id,
        period: p.period,
        period_id: p.id,
        medium: p.medium ?? "",
      };
    }),
  }));
};

const buildPeriodsFromTimetable = (timetableData) => {
  if (
    !timetableData ||
    !Array.isArray(timetableData.timetable) ||
    timetableData.timetable.length === 0
  ) {
    return [];
  }
  return (timetableData.timetable[0].periods || []).map((p) => ({
    id: String(p.period),
    label: String(p.period),
    time:
      p.break_start_time && p.break_end_time
        ? `${p.break_start_time} - ${p.break_end_time}`
        : "",
  }));
};

const LOCKED_STATUSES = ["submitted", "approved", "resubmitted", "completed"];

const ScheduleList = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);

  const user = TokenService.getUser() || {};
  const clientData = user?.client_data?.[0] || {};
  const academic_id = clientData?.academic_year_id || "";
  const schoolId =
    clientData.school_id != null && clientData.school_id !== ""
      ? Number(clientData.school_id)
      : null;
  const staffId =
    clientData.master_id != null && clientData.master_id !== ""
      ? Number(clientData.master_id)
      : null;

  const staff_Id = user.id != null && user.id !== "" ? Number(user.id) : null;
  const userId = user.id != null ? Number(user.id) : null;

  // Timetable state
  const [timetableRows, setTimetableRows] = useState([]);
  const [timetablePeriods, setTimetablePeriods] = useState([]);
  const [timetableLoading, setTimetableLoading] = useState(false);
  const [weekRange, setWeekRange] = useState("");

  const [selectedCells, setSelectedCells] = useState([]);

  // Lesson plan table state
  const [lessonPlans, setLessonPlans] = useState([]);
  const [lessonPlansLoading, setLessonPlansLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [columnFilters, setColumnFilters] = useState([]);

  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  const [modal, setModal] = useState({
    open: false,
    type: "info",
    message: "",
  });

  const closeModal = useCallback(
    () => setModal((prev) => ({ ...prev, open: false })),
    [],
  );
  /**
   * Unique key for a draft lesson-plan row.
   * Combines def_id + item_id since the same def_id can appear with
   * multiple item_ids (different versions / lesson planners).
   */
  const getRowKey = (plan) => {
    if (!plan) return null;
    const defId = plan.def_id ?? plan.defId;
    const itemId = plan.item_id ?? plan.itemId;
    if (defId == null || itemId == null) return null;
    return `${defId}__${itemId}`;
  };
  const legendItems = useMemo(
    () => [
      { key: "submitted", label: t.legendSubmitted || "Submitted" },
      { key: "approved", label: t.legendApproved || "Approved" },
      { key: "rejected", label: t.legendRejected || "Rejected" },
      { key: "resubmitted", label: t.legendResubmitted || "Re-Submitted" },
      { key: "completed", label: t.legendCompleted || "Completed" },
    ],
    [
      t.legendSubmitted,
      t.legendApproved,
      t.legendRejected,
      t.legendResubmitted,
      t.legendCompleted,
    ],
  );

  // const firstSelected = selectedCells[0];

  // ---------- Fetch weekly timetable ----------
  const fetchTimetable = useCallback(async () => {
    if (!schoolId || !staffId) return;
    try {
      setTimetableLoading(true);
      const today = dayjs().format("YYYY-MM-DD");
      const res = await AxiosInstance.post("/book/getStaffTimetable", {
        school_id: schoolId,
        staff_id: staffId,
        schedule_date: today,
        academic_year: academic_id,
      });
      if (res.data?.status && res.data.data) {
        const rows = transformTimetableResponse(res.data.data);
        setTimetableRows(rows);
        setTimetablePeriods(buildPeriodsFromTimetable(res.data.data));
        if (res.data.data.week_start && res.data.data.week_end) {
          setWeekRange(
            `${moment(res.data.data.week_start).format("MMM D")} - ${moment(
              res.data.data.week_end,
            ).format("MMM D, YYYY")}`,
          );
        }
      } else {
        setTimetableRows([]);
        setTimetablePeriods([]);
      }
    } catch (err) {
      console.error("Error fetching timetable:", err);
      setTimetableRows([]);
      setTimetablePeriods([]);
    } finally {
      setTimetableLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, staffId]);

  // ---------- Fetch lesson-plan drafts ----------
  const fetchLessonPlanDrafts = useCallback(
    async (cells) => {
      if (
        !schoolId ||
        !staffId ||
        !Array.isArray(cells) ||
        cells.length === 0
      ) {
        setLessonPlans([]);
        setRowCount(0);
        return;
      }

      const first = cells[0];
      const classId = first.rawPeriod?.class_id ?? null;
      const courseId = first.rawPeriod?.course_id ?? null;
      const subject = first.rawPeriod?.subject_name || first.subject || "";

      if (classId == null || !subject) {
        setLessonPlans([]);
        setRowCount(0);
        setModal({
          open: true,
          type: "error",
          message:
            t.invalidEmptyCell ||
            "Selected period is missing class or subject information.",
        });
        return;
      }

      const selected_periods = cells.map((c) => ({
        date: c.rawDate || c.date,
        section_id: c.rawPeriod?.section_id || "",
        period: Number(c.rawPeriod?.period || c.periodId),
      }));

      const searchFilter = columnFilters.find(
        (f) => f.id === "search" || f.id === "global",
      );
      const search = searchFilter?.value ? String(searchFilter.value) : "";

      const payload = {
        school_id: schoolId,
        staff_id: staff_Id,
        class_id: String(classId),
        subject: courseId != null ? String(courseId) : "",
        selected_periods,
        search,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      };

      try {
        setLessonPlansLoading(true);
        const res = await AxiosInstance.post(
          "/book/lessonPlanDraftList",
          payload,
        );

        if (res.data?.status) {
          const rows =
            res.data.data?.rows || res.data.data?.list || res.data.data || [];
          setLessonPlans(Array.isArray(rows) ? rows : []);
          setRowCount(
            res.data.data?.total ??
              res.data.data?.count ??
              (Array.isArray(rows) ? rows.length : 0),
          );
        } else {
          setLessonPlans([]);
          setRowCount(0);
          if (res.data?.message) {
            setModal({
              open: true,
              type: "error",
              message: res.data.message,
            });
          }
        }
      } catch (err) {
        console.error("Error fetching lesson plan drafts:", err);
        setLessonPlans([]);
        setRowCount(0);
        setModal({
          open: true,
          type: "error",
          message:
            err?.response?.data?.message ||
            t.somethingWentWrong ||
            "Something went wrong",
        });
      } finally {
        setLessonPlansLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      schoolId,
      staffId,
      pagination.pageIndex,
      pagination.pageSize,
      columnFilters,
      t,
    ],
  );

  useEffect(() => {
    fetchTimetable();
  }, [fetchTimetable]);

  useEffect(() => {
    fetchLessonPlanDrafts(selectedCells);
  }, [selectedCells, fetchLessonPlanDrafts]);

  const handleColumnFiltersChange = useCallback((updater) => {
    setColumnFilters((prev) =>
      typeof updater === "function" ? updater(prev) : updater,
    );
  }, []);

  // ---------- Cell click ----------
  const handleCellClick = useCallback(
    (cellInfo) => {
      const statusKey = resolveStatusKey(cellInfo.status);

      // 1. Block locked statuses (submitted/approved/resubmitted/completed)
      if (LOCKED_STATUSES.includes(statusKey)) {
        setModal({
          open: true,
          type: "error",
          message:
            t.invalidCellStatusSelection ||
            "You cannot schedule a period that is already submitted, approved, re-submitted, or completed.",
        });
        return;
      }

      const key = `${cellInfo.date}_${cellInfo.periodId}`;

      // 2. Toggle off if exact same cell clicked again
      const alreadySelectedIndex = selectedCells.findIndex(
        (c) => `${c.date}_${c.periodId}` === key,
      );
      if (alreadySelectedIndex !== -1) {
        setSelectedCells((prev) => {
          const next = prev.filter((_, i) => i !== alreadySelectedIndex);
          if (next.length === 0) {
            setSelectedPlan(null);
            setSelectedRowKey(null);
          }
          return next;
        });
        return;
      }

      // 3. Rejected cells are EXCLUSIVE:
      const cellIsRejected = statusKey === "rejected";

      if (selectedCells.length > 0) {
        const hasRejectedAlready = selectedCells.some(
          (c) => resolveStatusKey(c.status) === "rejected",
        );

        // (a) A rejected cell is already selected → block ANY further selection
        if (hasRejectedAlready) {
          setModal({
            open: true,
            type: "error",
            message:
              t.cannotAddWhenRejectedSelected ||
              "A rejected period is selected. Deselect it first before choosing other periods.",
          });
          return;
        }

        // (b) White cell(s) already selected and user clicks a rejected one → block
        if (cellIsRejected) {
          setModal({
            open: true,
            type: "error",
            message:
              t.cannotAddRejectedToOthers ||
              "A rejected period must be submitted on its own. Please clear other selected periods first.",
          });
          return;
        }
      }

      // 4. Block past dates (yesterday and earlier)
      const cellDateStr = cellInfo.rawDate || cellInfo.date;
      const cellDay = moment(cellDateStr).startOf("day");
      const today = moment().startOf("day");
      if (cellDay.isBefore(today)) {
        setModal({
          open: true,
          type: "error",
          message:
            t.cannotSelectPastDate ||
            "You cannot select a past date. Please choose today or a future date.",
        });
        return;
      }

      const cellSubject =
        cellInfo.rawPeriod?.subject_name || cellInfo.subject || "";
      const cellClass = cellInfo.rawPeriod?.class_name || "";
      const cellMedium = cellInfo.rawPeriod?.medium || cellInfo.medium || "";

      // 5. Block empty periods (no class/subject assigned)
      if (!cellSubject || !cellClass) {
        setModal({
          open: true,
          type: "error",
          message:
            t.invalidEmptyCell ||
            "This period has no class/subject assigned. Please select a valid period.",
        });
        return;
      }

      // 6. Same subject / same class / same medium checks against the first selected cell
      if (selectedCells.length > 0) {
        const first = selectedCells[0];
        const firstSubject =
          first.rawPeriod?.subject_name || first.subject || "";
        const firstClass = first.rawPeriod?.class_name || "";
        const firstMedium = first.rawPeriod?.medium || first.medium || "";

        if (
          firstSubject &&
          normalize(firstSubject) !== normalize(cellSubject)
        ) {
          setModal({
            open: true,
            type: "error",
            message:
              (t.invalidSubjectSelection ||
                "You can only select periods with the same subject") +
              ` (${firstSubject}).`,
          });
          return;
        }

        if (firstClass && normalize(firstClass) !== normalize(cellClass)) {
          setModal({
            open: true,
            type: "error",
            message:
              (t.invalidClassSelection ||
                "You can only select periods with the same class") +
              ` (${firstClass}).`,
          });
          return;
        }

        // 7. medium must match the first selected cell's medium (EM vs TA, etc.)
        if (
          firstMedium &&
          cellMedium &&
          normalize(firstMedium) !== normalize(cellMedium)
        ) {
          setModal({
            open: true,
            type: "error",
            message:
              (t.invalidMediumSelection ||
                "You can only select periods with the same medium") +
              ` (${firstMedium}).`,
          });
          return;
        }
      }

      // 8. Block if same subject + class + section combo is already selected.
      //    Same subject + same class + DIFFERENT section is still allowed.
      const cellClassId = cellInfo.rawPeriod?.class_id
        ? String(cellInfo.rawPeriod.class_id)
        : "";
      const cellSectionId = cellInfo.rawPeriod?.section_id
        ? String(cellInfo.rawPeriod.section_id)
        : "";

      const duplicateCombo = selectedCells.some((c) => {
        const existingSubject = c.rawPeriod?.subject_name || c.subject || "";
        const existingClassId = c.rawPeriod?.class_id
          ? String(c.rawPeriod.class_id)
          : "";
        const existingSectionId = c.rawPeriod?.section_id
          ? String(c.rawPeriod.section_id)
          : "";
        return (
          normalize(existingSubject) === normalize(cellSubject) &&
          existingClassId === cellClassId &&
          existingSectionId === cellSectionId
        );
      });

      if (duplicateCombo) {
        const comboLabel = `${cellSubject} ${cellClass}${
          cellSectionId ? ` - ${cellSectionId}` : ""
        }`.trim();
        setModal({
          open: true,
          type: "error",
          message:
            t.duplicateSectionSelection ||
            `You have already selected ${comboLabel}. You can select a different section of the same class, but not the same section twice.`,
        });
        return;
      }

      // 9. Reset lesson-plan selection if this is the first cell being added
      if (selectedCells.length === 0) {
        setSelectedPlan(null);
        setSelectedRowKey(null);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }

      setSelectedCells((prev) => [...prev, cellInfo]);
    },
    [selectedCells, t],
  );

  const selectedCellsForTable = useMemo(
    () =>
      selectedCells.map((c) => ({
        date: c.date,
        periodId: c.periodId,
      })),
    [selectedCells],
  );

  // ---------- Checkbox toggle (driven by hm_pk) ----------
  const handleLessonPlanSelect = useCallback(
    (plan) => {
      if (selectedCells.length === 0) {
        setModal({
          open: true,
          type: "error",
          message:
            t.selectTimetableCellFirst ||
            "Please select a timetable cell first before choosing a lesson plan.",
        });
        return;
      }

      const rowKey = getRowKey(plan);
      if (!rowKey) return; // row missing def_id/item_id — nothing to key by

      setSelectedRowKey((prev) => {
        // Toggle off if same row is clicked again
        if (prev === rowKey) {
          setSelectedPlan(null);
          return null;
        }
        setSelectedPlan(plan);
        return rowKey;
      });
    },
    [selectedCells, t],
  );

  const handleView = useCallback(
    (row) =>
      navigate("/user/lessonplanview", {
        state: { ID: getPlanId(row), data: row },
      }),
    [navigate],
  );

  const columns = useMemo(
    () => [
      {
        id: "select",
        header: "",
        enableSorting: false,
        enableColumnFilter: false,
        size: 60,
        Cell: ({ row }) => {
          const plan = row.original;
          const rowKey = getRowKey(plan);

          // Checkbox is checked when its hm_pk matches the selected one.
          // For rows without an hm_pk we fall back to plan-id matching
          // so they remain selectable.
          const isSelected = rowKey != null && rowKey === selectedRowKey;
          return (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handleLessonPlanSelect(plan)}
              style={{
                width: 18,
                height: 18,
                cursor: "pointer",
                accentColor: "var(--color-primary)",
              }}
            />
          );
        },
      },
      { accessorKey: "topic", header: t.topic || "Topic" },
      { accessorKey: "sub_topic", header: t.subTopic || "Sub Topic" },
      {
        accessorKey: "lesson_planner",
        header: t.lessonPlan || "Lesson Planner",
      },
      { accessorKey: "subject_name", header: t.subject || "Subject" },
      { accessorKey: "class_name", header: t.class || "Class" },
      { accessorKey: "medium", header: t.medium || "Medium" },
      {
        id: "actions",
        header: t.action || "Action",
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={t.view || "View"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, selectedRowKey, selectedPlan, handleLessonPlanSelect, handleView],
  );

  // ---------- Submit to HM ----------
  const handleSubmitToHM = useCallback(async () => {
    if (selectedCells.length === 0) {
      setModal({
        open: true,
        type: "error",
        message:
          t.selectTimetableCellFirst ||
          "Please select a timetable cell before submitting.",
      });
      return;
    }
    if (!selectedPlan) {
      setModal({
        open: true,
        type: "error",
        message:
          t.selectLessonPlanFirst ||
          "Please select a lesson plan before submitting.",
      });
      return;
    }

    const plan = selectedPlan;
    const first = selectedCells[0];

    const selected_periods = selectedCells.map((c) => ({
      date: c.rawDate || c.date,
      section_id: c.rawPeriod?.section_id || "",
      period: Number(c.rawPeriod?.period || c.periodId),
    }));

    const scheduleDate =
      selected_periods
        .map((p) => p.date)
        .filter(Boolean)
        .sort()[0] || formatDate(new Date());
    const cellHmId = first.rawPeriod?.hm_id;
    // const planHmPk = getHmPk(plan);
    const payload = {
      id: cellHmId != null ? cellHmId : "",
      lesson_plan_id:
        plan.lesson_plan_id != null ? plan.lesson_plan_id : getPlanId(plan),
      school_id: schoolId,
      staff_id: staffId,
      item_id: selectedPlan?.item_id || "",
      version: plan.version != null ? Number(plan.version) : 1,
      content: plan.content || "",
      start_page: plan.start_page ?? null,
      end_page: plan.end_page ?? null,
      status: 1,
      class_id:
        first.rawPeriod?.class_id != null
          ? Number(first.rawPeriod.class_id)
          : null,
      course_id:
        plan.course_id != null
          ? Number(plan.course_id)
          : first.rawPeriod?.course_id != null
            ? Number(first.rawPeriod.course_id)
            : null,
      selected_periods,
      schedule_date: scheduleDate,
      created_by: userId,
    };

    try {
      setSubmitting(true);
      const res = await AxiosInstance.post("/book/lessonPlan-save", payload);

      if (res.data?.status) {
        setModal({
          open: true,
          type: "success",
          message:
            res.data.message ||
            t.lessonPlanScheduledSuccess ||
            "Lesson plan submitted successfully",
        });
        setSelectedCells([]);
        setSelectedPlan(null);
        fetchTimetable();
      } else {
        setModal({
          open: true,
          type: "error",
          message:
            res.data?.message ||
            t.failedToScheduleLessonPlan ||
            "Failed to submit lesson plan",
        });
      }
    } catch (err) {
      console.error("Error submitting to HM:", err);
      setModal({
        open: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          t.somethingWentWrong ||
          "Something went wrong",
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    selectedCells,
    selectedPlan,
    schoolId,
    staffId,
    userId,
    fetchTimetable,
    t,
  ]);

  const handleCancel = useCallback(() => navigate(-1), [navigate]);

  const submitDisabled =
    submitting || selectedCells.length === 0 || !selectedPlan;

  return (
    <>
      <PopupModal
        open={modal.open}
        title={
          modal.type === "success"
            ? t.modalSuccessTitle || "Success"
            : t.modalFailedTitle || "Failed"
        }
        message={modal.message}
        onOk={closeModal}
        onClose={closeModal}
      />

      {/* ============ Weekly Timetable ============ */}
      <div className={`${styles.container} mb-3`}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt aria-hidden />
          </div>
          <h2 className={styles.headerTitle}>
            {t.weeklyTimetable || "Weekly Timetable"}
          </h2>
          {weekRange && (
            <div style={{ marginLeft: "auto" }}>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.cancelButton}
                fullWidth={false}
                // onClick={() => {}}
                disabled
              >
                <FaCalendarAlt style={{ marginRight: 8 }} aria-hidden />
                {weekRange}
              </CustomButton>
            </div>
          )}
        </div>

        {timetableLoading ? (
          <div style={{ textAlign: "center", padding: 30 }}>
            {t.loadingTimetable || "Loading timetable..."}
          </div>
        ) : timetableRows.length > 0 ? (
          <ScheduleTable
            periods={timetablePeriods}
            schedule={timetableRows}
            showLegend
            legendItems={legendItems}
            selectable
            selectedCells={selectedCellsForTable}
            onCellClick={handleCellClick}
          />
        ) : (
          <div style={{ textAlign: "center", padding: 30 }}>
            {t.noTimetableFound || "No timetable found for this week."}
          </div>
        )}
      </div>

      {/* ============ Lesson Plan Table ============ */}
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaClipboardList aria-hidden />
          </div>
          <h2 className={styles.headerTitle}>
            {t.lessonPlan || "Lesson Plan"}
          </h2>
        </div>

        {selectedCells.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: 30,
              color: "#6b7280",
            }}
          >
            {t.selectTimetableCellFirst ||
              "Please select a timetable cell to view available lesson plans."}
          </div>
        ) : (
          <CustomMRTTable
            columns={columns}
            data={lessonPlans}
            loading={lessonPlansLoading}
            pagination={pagination}
            rowCount={rowCount}
            onPaginationChange={setPagination}
            columnFilters={columnFilters}
            onColumnFiltersChange={handleColumnFiltersChange}
            enableColumnFilters={true}
            enableGlobalFilter={false}
            hideGlobalSearch={false}
          />
        )}

        {/* Footer buttons */}
        <div
          className={styles.actions}
          style={{ justifyContent: "center", marginTop: 24, gap: 16 }}
        >
          <CustomButton
            type="button"
            variant="contained"
            className={styles.cancelButton}
            fullWidth={false}
            onClick={handleCancel}
          >
            {t.cancelButton || "Cancel"}
          </CustomButton>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.addButton}
            fullWidth={false}
            onClick={handleSubmitToHM}
            disabled={submitDisabled}
          >
            {submitting
              ? t.submitting || "Submitting..."
              : t.submitToHM || "Submit to HM"}
          </CustomButton>
        </div>
      </div>
    </>
  );
};

export default ScheduleList;
